// 한국환경공단_화학물질 정보 조회 서비스 (구성성분 검색용)
// 공공데이터포털 인증키는 더 이상 브라우저에서 다루지 않는다 - 백엔드가 키 관리를 대신하고,
// 프론트는 서버 프록시 엔드포인트만 호출한다. (docs: api-msds-openapi.md)
//
// 주의: 이 백엔드 엔드포인트(/api/v1/msds/components)는 아직 미완성이다. 백엔드가 내려주는
// { name, casNo, code } 는 name 이 국문명(sbstnNmKor) 기준이라 국문명이 없는 물질은 빈 값으로
// 온다. 물질명은 화면에서 항상 영문으로 표기해야 해서, 한국환경공단 원본 응답(item.raw)의
// sbstnNmEng(영문명)을 우선 쓰고, 없으면 sbstnNmKor → 백엔드 name 순으로 대체한다.
// 매핑이 확정되면 여기와 PopupChemSubstanceSearch.vue, MSDSTable.vue 의 사용처를 함께 다시 확인해야 한다.
//
// 주요관리항목(구성성분 표의 12개 체크박스, MSDSTable.vue) 자동 반영용: 백엔드 응답은 정제된
// {name, casNo, code} 와 별개로 한국환경공단 원본 응답을 item.raw 에 그대로 실어준다.
// item.raw.typeList[].sbstnClsfTypeNm (예: "제한물질 세분류", "인체등유해성물질 세분류", ...) 을 사용한다.
// 분류명 표기가 정확히 일치하는 것만 반영하고(느슨한 매칭은 오탐 위험), raw/typeList 가 없으면
// 빈 객체를 반환해 기존 동작(체크 없음)을 그대로 유지한다.
//
// NCIS typeList 파싱 규칙(2025-08-07 개정 화관법·화평법 기준, 자세한 근거는 plan.md 참고 문서 참조):
// - sbstnClsfTypeNm 은 "세분류" 접미사 등 노이즈가 섞여 오므로 정규화 후 비교해야 한다.
// - 인체급성/인체만성/생태유해성물질은 독립된 타입이 아니라 "인체등유해성물질" 타입 하나의
//   contInfo 문자열("인체급성유해성 : 25%, 인체만성유해성 : 0.1%, ...")을 분해해서 얻는다.
import HttpRequest from "@/util/HttpRequest";
import { extractErrorMessage } from "./apiErrorUtil";

// 주요관리항목 12종. 이 중 한국환경공단 물질분류(typeList)로 실제 판별 가능한 것은 8종
// (인체급성/인체만성/생태유해성/허가/제한/금지/사고대비/배출량조사대상)뿐이다. 나머지 4종
// (특별관리물질/관리대상물질/작업환경측정/특수건강검진)은 산업안전보건법(안전보건공단) 쪽
// 분류라 이 API 응답에는 포함되지 않는다 - MSDSTable.vue 의 행 스키마와 맞추기 위해 목록
// 자체는 12개를 그대로 export 하고, 판별 불가 4종은 항상 매치되지 않는 채로 둔다.
export const MAIN_MANAGEMENT_FIELDS = ["특별관리물질", "관리대상물질", "작업환경측정", "특수건강검진", "인체급성유해성물질", "인체만성유해성물질", "생태유해성물질", "허가물질", "제한물질", "금지물질", "사고대비물질", "배출량조사대상물질"];

// typeList 로 직접 판별 가능한 8종 중, sbstnClsfTypeNm 값 자체가 컬럼명과 정확히 일치하는 5종
// (인체급성/인체만성/생태유해성물질 3종은 "인체등유해성물질" 타입 하나에서 분해되므로 제외)
const 직접매칭_필드목록 = ["허가물질", "제한물질", "금지물질", "사고대비물질", "배출량조사대상물질"];

// 컬럼으로 매핑하지 않는 것이 확인된 타입(경고 로그 대상에서 제외). "기존화학물질"은 KE번호
// 조인 키 용도, "중점관리물질"은 화평법 별개 목록(산안법 특별관리물질과는 다른 리스트),
// "암, 돌연변이성물질 등"은 배출량조사대상물질과의 관계가 미확정이라 임의로 합치지 않는다.
const 컬럼외_기지타입목록 = ["기존화학물질", "중점관리물질", "암, 돌연변이성물질 등"];

// 인체등유해성물질 contInfo 안의 3종 키워드 → 컬럼명. 순서 무관, 셋 다/일부만 등장 가능(배타적이지 않음).
const 인체등유해성_키워드_필드 = [
  ["인체급성유해성", "인체급성유해성물질"],
  ["인체만성유해성", "인체만성유해성물질"],
  ["생태유해성", "생태유해성물질"],
];

// 유니코드 NFC 정규화 + 공백 정리 + 말미 "세분류" 접미사 제거. NCIS 응답의 타입명 노이즈 대응.
function normalizeTypeName(raw) {
  return (raw || "")
    .normalize("NFC")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\s*세분류$/, "");
}

// "인체급성유해성 : 25%" 형태에서 유형별 Y 여부를 추출한다. 키워드+콜론+숫자(+%)를 한 세트로
// 우선 매칭하고, %/함량 없이 유형명만 오는 경우(실제 사례 존재)도 대비해 키워드 단독 등장도 Y로
// 인정하되 함량 결측을 로그로 남긴다(함량 없다고 N으로 떨어뜨리면 안 됨).
function parseHumanEtcHazard(contInfo, unqNo) {
  const text = contInfo || "";
  const result = {};
  for (const [키워드, 필드] of 인체등유해성_키워드_필드) {
    const withPercent = text.match(new RegExp(`${키워드}\\s*[:：]\\s*([\\d.]+)\\s*%`));
    if (withPercent) {
      result[필드] = true;
    } else if (text.includes(키워드)) {
      result[필드] = true;
      // eslint-disable-next-line no-console
      console.warn(`[kecoChemApi] 인체등유해성물질(${unqNo || "unqNo 없음"})에 "${키워드}" 유형은 있으나 지정함량(%)을 찾지 못함:`, contInfo);
    }
  }
  return result;
}

function mapMainManagementItems(typeList) {
  const result = {};
  for (const type of typeList || []) {
    const rawName = type && type.sbstnClsfTypeNm;
    const name = normalizeTypeName(rawName);
    if (!name) continue;

    if (name === "인체등유해성물질") {
      Object.assign(result, parseHumanEtcHazard(type.contInfo, type.unqNo));
      continue;
    }
    if (직접매칭_필드목록.includes(name)) {
      result[name] = true;
      continue;
    }
    if (컬럼외_기지타입목록.includes(name)) continue;

    // 화이트리스트에 없는 타입 - 고시 개정으로 타입명이 바뀌면(실제로 2025-08-07에 "유독물질" →
    // "인체등유해성물질"로 변경된 전례가 있음) 컬럼 하나가 조용히 비어버릴 수 있으므로 반드시 로그.
    // eslint-disable-next-line no-console
    console.warn(`[kecoChemApi] 알 수 없는 물질분류 타입: "${rawName}" (정규화: "${name}") - 주요관리항목에 반영되지 않음`, type);
  }
  return result;
}

// 물질명(영문) 우선 표기: 한국환경공단 원본 필드(sbstnNmEng)를 우선 쓰고, 영문명이 없는
// 물질만 국문명 → 백엔드 name 순으로 대체한다.
function pickChemName(item) {
  const raw = item.raw || {};
  return raw.sbstnNmEng || raw.sbstnNmKor || item.name || "";
}

// searchType: "name"(기본) / "cas"
export async function searchChemSubstance({ searchNm, searchType = "name", numOfRows = 20, pageNo = 1 }) {
  try {
    const result = await HttpRequest.get(
      "/api/v1/msds/components",
      { params: { q: searchNm, searchType, page: pageNo, perPage: numOfRows } },
      true
    );
    const items = result.items || [];
    return items.map((item) => ({
      name: pickChemName(item),
      casNo: item.casNo,
      code: item.code,
      주요관리항목: mapMainManagementItems(item.raw && item.raw.typeList),
    }));
  } catch (err) {
    throw new Error(extractErrorMessage(err, "화학물질 정보를 불러오지 못했습니다."));
  }
}
